/*
 * Copyright (C) 2017 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that they will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */
import React, { Component } from 'react';
import { themeable } from '@instructure/ui-themeable';
import moment from 'moment-timezone';
import { Button } from '@instructure/ui-buttons';
import { Pill } from '@instructure/ui-elements';
import { PresentationContent, ScreenReaderContent } from '@instructure/ui-a11y';
import { IconXLine } from '@instructure/ui-icons';
import { bool, string, number, func, object } from 'prop-types';
import { getFullDateAndTime } from '../../utilities/dateUtils';
import formatMessage from '../../format-message';
import { animatable } from '../../dynamic-ui';
const styles = {
  componentId: 'eAJaG',
  template: function template(theme) {
    return "\n\n.root_2_RE{background:".concat(theme.background || 'inherit', ";box-sizing:border-box;color:").concat(theme.color || 'inherit', ";font-family:").concat(theme.fontFamily || 'inherit', ";font-size:").concat(theme.fontSize || 'inherit', ";font-weight:").concat(theme.fontWeight || 'inherit', ";line-height:").concat(theme.lineHeight || 'inherit', ";padding:").concat(theme.padding || 'inherit', ";position:relative}\n\n.oppNameAndTitle_2_RE{max-width:16.5rem}\n\n.oppName_2_RE{box-sizing:border-box;color:").concat(theme.secondaryColor || 'inherit', ";flex:1;font-size:").concat(theme.nameFontSize || 'inherit', ";letter-spacing:0.0625rem;margin-right:").concat(theme.closeButtonIconSize || 'inherit', ";min-width:1px;padding-top:").concat(theme.namePaddingTop || 'inherit', ";text-transform:uppercase}\n\n.title_2_RE{margin-bottom:").concat(theme.titleMargin || 'inherit', "}\n\n.close_2_RE{offset-inline-end:0;position:absolute;top:0}\n\n[dir=ltr] .close_2_RE{right:0}\n\n[dir=rtl] .close_2_RE{left:0}\n\n.oppName_2_RE,.title_2_RE{-ms-hyphens:auto;-webkit-hyphens:auto;hyphens:auto;overflow-wrap:break-word;word-wrap:break-word}\n\n.footer_2_RE{-webkit-padding-end:").concat(theme.footerPadding || 'inherit', ";align-items:flex-end;box-sizing:border-box;display:flex;justify-content:space-between;padding-inline-end:").concat(theme.footerPadding || 'inherit', "}\n\n[dir=ltr] .footer_2_RE{padding-right:").concat(theme.footerPadding || 'inherit', "}\n\n[dir=rtl] .footer_2_RE{padding-left:").concat(theme.footerPadding || 'inherit', "}\n\n.status_2_RE{-webkit-padding-end:").concat(theme.statusPadding || 'inherit', ";box-sizing:border-box;flex-grow:1;min-width:1px;padding-inline-end:").concat(theme.statusPadding || 'inherit', "}\n\n[dir=ltr] .status_2_RE{padding-right:").concat(theme.statusPadding || 'inherit', "}\n\n[dir=rtl] .status_2_RE{padding-left:").concat(theme.statusPadding || 'inherit', "}\n\n.points_2_RE{box-sizing:border-box;color:").concat(theme.secondaryColor || 'inherit', ";flex-shrink:0;font-size:").concat(theme.pointsFontSize || 'inherit', ";line-height:").concat(theme.pointsLineHeight || 'inherit', ";min-width:1px;text-align:end;text-transform:uppercase}\n\n[dir=ltr] .points_2_RE{text-align:right}\n\n[dir=rtl] .points_2_RE{text-align:left}\n\n.pointsNumber_2_RE{display:block;font-size:").concat(theme.pointsNumberFontSize || 'inherit', "}\n\n.due_2_RE{color:").concat(theme.secondaryColor || 'inherit', ";font-size:").concat(theme.dueFontSize || 'inherit', ";margin-top:").concat(theme.dueMargin || 'inherit', "}\n\n.dueText_2_RE{font-weight:").concat(theme.dueTextFontWeight || 'inherit', "}");
  },
  'root': 'root_2_RE',
  'oppNameAndTitle': 'oppNameAndTitle_2_RE',
  'oppName': 'oppName_2_RE',
  'title': 'title_2_RE',
  'close': 'close_2_RE',
  'footer': 'footer_2_RE',
  'status': 'status_2_RE',
  'points': 'points_2_RE',
  'pointsNumber': 'pointsNumber_2_RE',
  'due': 'due_2_RE',
  'dueText': 'dueText_2_RE'
};
import theme from './theme.js';
export class Opportunity extends Component {
  constructor(props) {
    super(props);

    this.linkRef = ref => {
      this.link = ref;
    };

    this.dismiss = () => {
      if (this.props.dismiss) {
        this.props.dismiss(this.props.id, this.props.plannerOverride);
      }
    };

    const tzMomentizedDate = moment.tz(props.dueAt, props.timeZone);
    this.fullDate = getFullDateAndTime(tzMomentizedDate);
  }

  componentDidMount() {
    this.props.registerAnimatable('opportunity', this, this.props.animatableIndex, [this.props.id]);
  }

  componentWillReceiveProps(newProps) {
    this.props.deregisterAnimatable('opportunity', this, [this.props.id]);
    this.props.registerAnimatable('opportunity', this, newProps.animatableIndex, [newProps.id]);
  }

  componentWillUnmount() {
    this.props.deregisterAnimatable('opportunity', this, [this.props.id]);
  }

  getFocusable() {
    return this.link;
  }

  renderButton() {
    const isDismissed = this.props.plannerOverride && this.props.plannerOverride.dismissed;
    return React.createElement("div", {
      className: styles.close
    }, isDismissed ? null : React.createElement(Button, {
      onClick: this.dismiss,
      variant: "icon",
      icon: IconXLine,
      size: "small",
      title: formatMessage('Dismiss {opportunityName}', {
        opportunityName: this.props.opportunityTitle
      })
    }, React.createElement(ScreenReaderContent, null, formatMessage('Dismiss {opportunityName}', {
      opportunityName: this.props.opportunityTitle
    }))));
  }

  renderPoints() {
    if (typeof this.props.points !== 'number') {
      return React.createElement(ScreenReaderContent, null, formatMessage('There are no points associated with this item'));
    }

    return React.createElement("div", {
      className: styles.points
    }, React.createElement(ScreenReaderContent, null, formatMessage('{points} points', {
      points: this.props.points
    })), React.createElement(PresentationContent, null, React.createElement("span", {
      className: styles.pointsNumber
    }, this.props.points), formatMessage('points')));
  }

  render() {
    return React.createElement("div", {
      className: styles.root
    }, React.createElement("div", {
      className: styles.oppNameAndTitle
    }, React.createElement("div", {
      className: styles.oppName
    }, this.props.courseName), React.createElement("div", {
      className: styles.title
    }, React.createElement(Button, {
      variant: "link",
      theme: {
        mediumPadding: '0',
        mediumHeight: 'normal'
      },
      href: this.props.url,
      buttonRef: this.linkRef
    }, this.props.opportunityTitle))), React.createElement("div", {
      className: styles.footer
    }, React.createElement("div", {
      className: styles.status
    }, React.createElement(Pill, {
      text: formatMessage('Missing'),
      variant: "danger"
    }), React.createElement("div", {
      className: styles.due
    }, React.createElement("span", {
      className: styles.dueText
    }, formatMessage('Due:')), " ", this.fullDate)), this.renderPoints()), this.renderButton());
  }

}
Opportunity.propTypes = {
  id: string.isRequired,
  dueAt: string.isRequired,
  points: number,
  courseName: string.isRequired,
  opportunityTitle: string.isRequired,
  timeZone: string.isRequired,
  url: string.isRequired,
  dismiss: func,
  plannerOverride: object,
  registerAnimatable: func,
  deregisterAnimatable: func,
  animatableIndex: number
};
Opportunity.defaultProps = {
  registerAnimatable: () => {},
  deregisterAnimatable: () => {},
  dismiss: () => {}
};
export default animatable(themeable(theme, styles)(Opportunity));